import React from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Eye, Film, Clock } from "lucide-react";
import LessonContent from "@/components/LessonContent";

/**
 * Instructor-side "Preview as student" modal: renders the lesson HTML exactly
 * how students will see it in Learn.jsx, plus the saved storyboard if present.
 *
 * Intentionally does NOT render the audio player here — the audio endpoint
 * requires a saved lesson + enrolled student, and this preview is shown on
 * the unsaved draft. The student view itself carries the real audio player.
 */
export default function StudentPreviewDialog({
    open,
    onOpenChange,
    title,
    chapterTitle,
    content,
    storyboard,
    contentType = "text",
    videoUrl = "",
}) {
    const slides = storyboard?.slides || [];
    const mins = storyboard
        ? Math.max(1, Math.round((storyboard.estimated_duration_sec || slides.length * 35) / 60))
        : 0;
    // The real Learn view shows the rendered MP4 whenever `video_url` is set,
    // independent of `content_type`. Mirroring that here means the preview
    // accurately reflects what a student will see — previously the preview
    // only honoured `content_type === "video"`, hiding rendered AI videos
    // attached to text/slideshow lessons.
    const hasVideo = !!videoUrl;
    const hasAnything =
        (content && content.trim()) || slides.length > 0 || hasVideo;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl max-h-[92vh] overflow-hidden p-0"
                data-testid="student-preview-dialog"
            >
                <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent font-semibold">
                        <Eye className="h-3.5 w-3.5" />
                        Student preview
                    </div>
                    <DialogTitle className="font-display text-2xl text-primary">
                        {title || "Untitled lesson"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Preview how this lesson will appear to a student in the learner portal.
                    </DialogDescription>
                    {chapterTitle ? (
                        <div className="text-xs text-muted-foreground">
                            Chapter · {chapterTitle}
                        </div>
                    ) : null}
                </DialogHeader>

                <ScrollArea className="max-h-[72vh] px-6 py-5">
                    {!hasAnything ? (
                        <div className="text-center py-12 text-sm text-muted-foreground">
                            Nothing to preview yet. Add some lesson content or generate a storyboard.
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {hasVideo ? (
                                <video
                                    src={videoUrl}
                                    controls
                                    className="w-full rounded-sm border border-border aspect-video bg-black"
                                    data-testid="student-preview-video"
                                />
                            ) : null}

                            <LessonContent content={content} />

                            {slides.length > 0 ? (
                                <section
                                    className="mt-6 pt-6 border-t border-border"
                                    data-testid="student-preview-storyboard"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-display text-xl text-primary flex items-center gap-2">
                                            <Film className="h-4 w-4" />
                                            Video storyboard
                                        </h3>
                                        <div className="flex gap-2">
                                            <Badge variant="outline" className="gap-1 text-xs">
                                                <Clock className="h-3 w-3" />
                                                ~{mins} min
                                            </Badge>
                                            <Badge variant="secondary" className="text-xs">
                                                {slides.length} slides
                                            </Badge>
                                        </div>
                                    </div>
                                    <ol className="space-y-3">
                                        {slides.map((s, idx) => (
                                            <li
                                                key={idx}
                                                className="rounded-lg border border-border bg-muted/30 p-4"
                                            >
                                                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                                                    Slide {s.number || idx + 1}
                                                </div>
                                                <h4 className="font-display text-base text-primary mb-2">
                                                    {s.heading || "Untitled"}
                                                </h4>
                                                {(s.bullets || []).length > 0 && (
                                                    <ul className="list-disc pl-5 text-sm text-foreground/90 mb-2 space-y-0.5">
                                                        {(s.bullets || []).map((b, i) => (
                                                            <li key={i}>{b}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                                {s.narration ? (
                                                    <p className="text-sm italic text-foreground/75 leading-relaxed border-l-2 border-accent pl-3">
                                                        {s.narration}
                                                    </p>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ol>
                                </section>
                            ) : null}
                        </div>
                    )}
                </ScrollArea>

                <DialogFooter className="px-6 py-4 border-t border-border">
                    <Button
                        onClick={() => onOpenChange(false)}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="student-preview-close"
                    >
                        Back to editor
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
